/**
 * Variant Availability Handler
 * Dynamically shows/hides variant options based on actual variant combinations
 * and applies sold-out styling when all matching variants are unavailable
 */

class VariantAvailability {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.variantPicker = document.querySelector('#variant-select-' + sectionId);
    
    if (!this.variantPicker || !window.hideUnavailableOptions) {
      return;
    }

    // Get variant data from the script tag
    var variantDataScript = this.variantPicker.querySelector('[data-variant-options]');
    if (!variantDataScript) {
      return;
    }

    try {
      this.variantData = JSON.parse(variantDataScript.textContent);
    } catch (e) {
      return;
    }

    // Build variant lookup map
    this.buildVariantMap();
    
    // Track previous selections to detect changes
    this.previousSelections = [];
    
    // Add debounce to prevent rapid re-triggers
    this.updateTimeout = null;
    
    // Flag to prevent cascading updates from auto-selection
    this.isAutoSelecting = false;
    
    // Initialize availability after a small delay to ensure DOM is ready
    var self = this;
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function() {
        self.updateAvailability();
        // Mark as initialized for CSS animations
        self.variantPicker.classList.add('variant-availability-initialized');
      });
    } else {
      setTimeout(function() {
        self.updateAvailability();
        // Mark as initialized for CSS animations
        self.variantPicker.classList.add('variant-availability-initialized');
      }, 0);
    }
    
    // Listen for option changes
    this.variantPicker.addEventListener('change', function(e) {
      // Skip if this change is from auto-selection during an update
      if (self.isAutoSelecting) {
        return;
      }
      
      // Clear any pending update
      if (self.updateTimeout) {
        clearTimeout(self.updateTimeout);
      }
      
      // Debounce: Only update after events stop firing for 10ms
      self.updateTimeout = setTimeout(function() {
        if (window.requestAnimationFrame) {
          requestAnimationFrame(function() {
            self.updateAvailability();
          });
        } else {
          self.updateAvailability();
        }
      }, 10);
    });
  }

  /**
   * Build a map of all variants for quick lookup
   * Format: { "option1Value|option2Value|option3Value": {available: true/false} }
   */
  buildVariantMap() {
    this.variantMap = new Map();
    this.optionCount = this.variantData.options.length;
    
    this.variantData.variants.forEach(function(variant) {
      // Create key from all option values
      var key = variant.options.join('|');
      this.variantMap.set(key, {
        available: variant.available
      });
    }.bind(this));
  }

  /**
   * Get currently selected values for each option position
   */
  getSelectedOptions() {
    var selectedOptions = [];
    var fieldsets = this.variantPicker.querySelectorAll('.product-form__input--variant-picker');
    
    fieldsets.forEach(function(fieldset, index) {
      var selected = fieldset.querySelector('input[type="radio"]:checked, option[selected]');
      if (selected) {
        selectedOptions[index] = selected.value;
      } else {
        selectedOptions[index] = null;
      }
    });
    
    return selectedOptions;
  }

  /**
   * Update which options are available based on current selections
   */
  updateAvailability() {
    var selectedOptions = this.getSelectedOptions();
    var fieldsets = this.variantPicker.querySelectorAll('.product-form__input--variant-picker');
    
    // Check if primary option (option 0) has changed
    var primaryOptionChanged = this.previousSelections.length > 0 && 
                                 this.previousSelections[0] !== selectedOptions[0];
    
    var self = this;
    
    // If primary option changed, hide sub-variants FIRST to prevent flicker
    if (primaryOptionChanged) {
      // Add updating class to hide sub-variants during recalculation
      fieldsets.forEach(function(fieldset, optionIndex) {
        if (optionIndex > 0) { // Only secondary options
          fieldset.classList.add('is-updating');
        }
      });
      
      // Force browser to render the is-updating class before continuing
      // This prevents DOM changes from appearing before fade-out starts
      if (fieldsets.length > 1) {
        void fieldsets[1].offsetHeight; // Force reflow
      }
    }
    
    // Set flag to prevent auto-selection from triggering another update
    this.isAutoSelecting = true;
    
    // Do the actual availability calculation
    fieldsets.forEach(function(fieldset, optionIndex) {
      var optionElements = fieldset.querySelectorAll('.dynamic-option');
      
      optionElements.forEach(function(optionElement) {
        var input = optionElement.querySelector('input[type="radio"], option');
        var label = optionElement.querySelector('label');
        if (!input) return;
        
        var optionValue = input.value;
        
        // Check if this option value is available (exists in at least one variant)
        var isAvailable = self.isOptionAvailable(optionValue, optionIndex, selectedOptions);
        
        // Check if this option value is sold out (all matching variants are unavailable)
        var isSoldOut = self.isOptionSoldOut(optionValue, optionIndex, selectedOptions);
        
        // Show/hide the option
        if (isAvailable) {
          optionElement.classList.remove('hidden');
          
          // Add or remove soldout class on the label
          if (label) {
            if (isSoldOut) {
              label.classList.add('soldout');
            } else {
              label.classList.remove('soldout');
            }
          }
        } else {
          optionElement.classList.add('hidden');
        }
      });
    });
    
    // After updating all visibility and sold-out states, ensure valid selections
    // Process in order (option 0, then 1, then 2) so dependencies are handled correctly
    fieldsets.forEach(function(fieldset, optionIndex) {
      self.ensureValidSelection(fieldset, optionIndex, primaryOptionChanged);
    });
    
    // Clear the auto-selecting flag after all selections are complete
    this.isAutoSelecting = false;
    
    // Store current selections for next comparison (use slice for IE compatibility)
    this.previousSelections = selectedOptions.slice();
    
    // If primary changed, remove updating class after fade-out completes
    if (primaryOptionChanged) {
      // Wait for fade-out animation to complete (200ms) + small buffer before fading in
      setTimeout(function() {
        fieldsets.forEach(function(fieldset, optionIndex) {
          if (optionIndex > 0) {
            fieldset.classList.remove('is-updating');
          }
        });
      }, 250); // 200ms fade-out + 50ms buffer for slower devices
    }
  }

  /**
   * Ensure a valid option is selected, auto-select if current is hidden
   * Prioritize available (in-stock) options over sold-out ones
   * For secondary options (index > 0), always auto-select when primary changes
   */
  ensureValidSelection(fieldset, optionIndex, primaryOptionChanged) {
    primaryOptionChanged = primaryOptionChanged || false;
    var currentSelected = fieldset.querySelector('input[type="radio"]:checked, option[selected]');
    
    // For secondary options (not the first option), auto-select when:
    // 1. Current selection is hidden/invalid, OR
    // 2. Primary option has changed (force re-selection)
    if (optionIndex > 0) {
      var shouldAutoSelect = primaryOptionChanged;
      
      // Check if current selection is still valid (visible and exists)
      if (currentSelected) {
        var parentLi = currentSelected.closest('.dynamic-option');
        var isHidden = parentLi && parentLi.classList.contains('hidden');
        
        // Auto-select if hidden OR if primary option changed
        if (isHidden || shouldAutoSelect) {
          this.autoSelectFirstAvailable(fieldset);
        }
      } else {
        // Nothing selected at all, auto-select
        this.autoSelectFirstAvailable(fieldset);
      }
    } else {
      // For primary option (index 0), only auto-select if current is hidden or nothing selected
      if (currentSelected) {
        var parentLi = currentSelected.closest('.dynamic-option');
        if (parentLi && parentLi.classList.contains('hidden')) {
          this.autoSelectFirstAvailable(fieldset);
        }
      } else {
        this.autoSelectFirstAvailable(fieldset);
      }
    }
  }

  /**
   * Auto-select the first available (preferably in-stock) option
   */
  autoSelectFirstAvailable(fieldset) {
    // Strategy: Prefer in-stock options, but select sold-out if that's all that's available
    
    // First try: visible AND not sold out
    var targetInput = fieldset.querySelector('.dynamic-option:not(.hidden) input[type="radio"]:not(:checked)');
    if (targetInput) {
      var targetLabel = targetInput.nextElementSibling; // label comes after input
      if (targetLabel && !targetLabel.classList.contains('soldout')) {
        targetInput.checked = true;
        this.triggerChangeEvent(targetInput);
        return;
      }
    }
    
    // Second try: any visible option that's not sold out
    var allVisibleInputs = fieldset.querySelectorAll('.dynamic-option:not(.hidden) input[type="radio"]');
    for (var i = 0; i < allVisibleInputs.length; i++) {
      var input = allVisibleInputs[i];
      var label = input.nextElementSibling;
      if (label && !label.classList.contains('soldout')) {
        input.checked = true;
        this.triggerChangeEvent(input);
        return;
      }
    }
    
    // Third try: just pick the first visible one, even if sold out
    var firstVisible = fieldset.querySelector('.dynamic-option:not(.hidden) input[type="radio"]');
    if (firstVisible) {
      firstVisible.checked = true;
      this.triggerChangeEvent(firstVisible);
      return;
    }
    
    // Handle select dropdowns
    var firstVisibleOption = fieldset.querySelector('.dynamic-option:not(.hidden) option');
    if (firstVisibleOption) {
      firstVisibleOption.selected = true;
      this.triggerChangeEvent(firstVisibleOption.parentElement);
    }
  }

  /**
   * Trigger change event with browser compatibility
   */
  triggerChangeEvent(element) {
    if (typeof Event === 'function') {
      // Modern browsers
      element.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // IE compatibility
      var event = document.createEvent('Event');
      event.initEvent('change', true, true);
      element.dispatchEvent(event);
    }
  }

  /**
   * Check if an option value is sold out
   * (all matching variants are unavailable)
   */
  isOptionSoldOut(optionValue, optionIndex, selectedOptions) {
    // Build a pattern for this option value
    var testPattern = [];
    
    for (var i = 0; i < this.optionCount; i++) {
      if (i < optionIndex) {
        // For options BEFORE this one, use the selected value (must match)
        testPattern[i] = selectedOptions[i];
      } else if (i === optionIndex) {
        // For this option, use the value we're testing
        testPattern[i] = optionValue;
      } else {
        // For options AFTER this one, allow any value (wildcard)
        testPattern[i] = null;
      }
    }
    
    // Find all matching variants and check if ANY are available
    var hasAvailableVariant = false;
    
    for (var entry of this.variantMap) {
      var variantKey = entry[0];
      var variantData = entry[1];
      var variantOptions = variantKey.split('|');
      
      // Check if this variant matches the pattern
      var matches = true;
      for (var i = 0; i < testPattern.length; i++) {
        if (testPattern[i] !== null && testPattern[i] !== undefined && testPattern[i] !== variantOptions[i]) {
          matches = false;
          break;
        }
      }
      
      if (matches) {
        // Found a matching variant, check if it's available
        if (variantData.available) {
          hasAvailableVariant = true;
          break;
        }
      }
    }
    
    // If no available variant found, it's sold out
    return !hasAvailableVariant;
  }

  /**
   * Check if an option value is available given the current selections
   */
  isOptionAvailable(optionValue, optionIndex, selectedOptions) {
    // Build a test pattern with this value at the current position
    var testPattern = [];
    
    for (var i = 0; i < this.optionCount; i++) {
      if (i < optionIndex) {
        // For options BEFORE this one, use the selected value (must match)
        testPattern[i] = selectedOptions[i];
      } else if (i === optionIndex) {
        // For this option, use the value we're testing
        testPattern[i] = optionValue;
      } else {
        // For options AFTER this one, allow any value (wildcard)
        testPattern[i] = null;
      }
    }
    
    // Check if any variant matches this pattern
    return this.hasMatchingVariant(testPattern);
  }

  /**
   * Check if there's at least one variant matching the given pattern
   * null values in the pattern are wildcards (can match any value)
   */
  hasMatchingVariant(pattern) {
    // Check each variant to see if it matches the pattern
    for (var entry of this.variantMap) {
      var variantKey = entry[0];
      var variantOptions = variantKey.split('|');
      
      // Check if this variant matches the pattern
      var matches = true;
      for (var i = 0; i < pattern.length; i++) {
        // If pattern has a specific value (not null), it must match
        if (pattern[i] !== null && pattern[i] !== undefined && pattern[i] !== variantOptions[i]) {
          matches = false;
          break;
        }
      }
      
      if (matches) {
        return true;
      }
    }
    
    return false;
  }
}

// Initialize when DOM is ready
(function() {
  var initVariantAvailability = function() {
    var pickers = document.querySelectorAll('variant-radios, variant-selects');
    pickers.forEach(function(picker) {
      var sectionId = picker.dataset.section;
      if (sectionId && !picker.dataset.availabilityInitialized) {
        new VariantAvailability(sectionId);
        picker.dataset.availabilityInitialized = 'true';
      }
    });
  };
  
  // Initialize when DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantAvailability);
  } else {
    initVariantAvailability();
  }
  
  // Re-initialize when content changes (for product swaps, quick view, etc.)
  if (window.MutationObserver) {
    var observer = new MutationObserver(function(mutations) {
      var shouldReinit = false;
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length > 0) {
          for (var i = 0; i < mutation.addedNodes.length; i++) {
            var node = mutation.addedNodes[i];
            if (node.nodeType === 1) {
              // Check if the node itself is a variant picker
              if (node.matches && (node.matches('variant-radios') || node.matches('variant-selects'))) {
                shouldReinit = true;
                break;
              }
              // Check if the node contains variant pickers (for Quick View, drawers, etc.)
              if (node.querySelector) {
                var hasVariantPicker = node.querySelector('variant-radios, variant-selects');
                if (hasVariantPicker) {
                  shouldReinit = true;
                  break;
                }
              }
            }
          }
        }
      });
      if (shouldReinit) {
        // Use setTimeout to ensure DOM is fully updated
        setTimeout(function() {
          initVariantAvailability();
        }, 100);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  // Expose initialization function globally for manual triggering
  window.initVariantAvailability = initVariantAvailability;
})();
